'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import { cn } from 'cn';
import { List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useImportQueueRail } from '../context/import-queue-rail-context';
import type { ImportQueueController } from '../types/import-queue-controller';
import { isImportQueueHeaderRoute } from '../utils/is-import-queue-header-route';
import { ImportQueueListControls } from './import-queue-list-controls';
import { ImportQueueProgressSummary } from './import-queue-progress-summary';
import { ImportQueueSection } from './import-queue-section';
interface ImportQueueRailProps {
  controllers: ImportQueueController[];
}
function domainKeyForHeaderRoute(pathname: string): string | null {
  if (pathname === '/insights') return 'insights';
  if (pathname === '/templates') return 'templates';
  if (pathname === '/listings') return 'listings';
  return null;
}
export function ImportQueueRail(t0) {
  const $ = _c(80);
  const {
    controllers
  } = t0;
  const pathname = usePathname();
  const {
    isOpen,
    setOpen
  } = useImportQueueRail();
  let t1;
  if ($[0] !== pathname) {
    t1 = isImportQueueHeaderRoute(pathname);
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isHeaderRoute = t1;
  let hasItems;
  let t2;
  let t3;
  if ($[2] !== controllers || $[3] !== isHeaderRoute || $[4] !== pathname) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const controllersWithItems = controllers.filter(_temp);
      hasItems = controllersWithItems.length > 0;
      if (!hasItems && !isHeaderRoute) {
        t3 = null;
        break bb0;
      }
      let t4;
      if ($[8] !== pathname) {
        t4 = domainKeyForHeaderRoute(pathname);
        $[8] = pathname;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      const headerDomainKey = t4;
      let t5;
      if ($[10] !== controllers || $[11] !== headerDomainKey) {
        t5 = headerDomainKey ? controllers.find(c_0 => c_0.domainKey === headerDomainKey) : undefined;
        $[10] = controllers;
        $[11] = headerDomainKey;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      const headerDomainController = t5;
      bb1: {
        if (!isHeaderRoute || !headerDomainKey || !headerDomainController) {
          t2 = controllersWithItems;
          break bb1;
        }
        if (headerDomainController.items.length === 0) {
          let t6;
          if ($[13] !== headerDomainKey) {
            t6 = c_1 => c_1.domainKey !== headerDomainKey;
            $[13] = headerDomainKey;
            $[14] = t6;
          } else {
            t6 = $[14];
          }
          const otherDomainsWithItems = controllersWithItems.filter(t6);
          if (otherDomainsWithItems.length === 0) {
            let t7;
            if ($[15] !== headerDomainController) {
              t7 = [headerDomainController];
              $[15] = headerDomainController;
              $[16] = t7;
            } else {
              t7 = $[16];
            }
            t2 = t7;
            break bb1;
          }
          t2 = [headerDomainController, ...otherDomainsWithItems];
          break bb1;
        }
        t2 = controllersWithItems;
      }
    }
    $[2] = controllers;
    $[3] = isHeaderRoute;
    $[4] = pathname;
    $[5] = hasItems;
    $[6] = t2;
    $[7] = t3;
  } else {
    hasItems = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  const sectionControllers = t2;
  const multiSection = sectionControllers.length > 1;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[17] !== controllers || $[18] !== hasItems || $[19] !== isHeaderRoute || $[20] !== isOpen || $[21] !== multiSection || $[22] !== sectionControllers || $[23] !== setOpen) {
    const anyActiveWork = controllers.some(_temp2);
    const footerTitle = multiSection ? "Imports" : sectionControllers[0]?.title ?? "Imports";
    const footerSubtitle = multiSection ? "Recent uploads" : sectionControllers[0]?.subtitle ?? "";
    const footerListControlsController = !multiSection && sectionControllers[0] && (sectionControllers[0].listPreferences || sectionControllers[0].listActions) ? sectionControllers[0] : null;
    const summaryProgress = !multiSection && sectionControllers[0]?.progress && sectionControllers[0].progress.total > 1 ? sectionControllers[0].progress : null;
    t7 = "pointer-events-none fixed top-0 right-0 z-40 h-full w-0 overflow-visible";
    t8 = "ImportQueueRail";
    t9 = "import-queue-rail.tsx";
    t4 = "relative h-full";
    const t10 = !isOpen ? true : undefined;
    const t11 = !isOpen;
    const t12 = !isOpen;
    let t13;
    if ($[30] !== isOpen || $[31] !== t12) {
      t13 = cn("bg-card border-border absolute top-0 flex h-full w-[min(20rem,100vw)] flex-col rounded-l-xl border-y border-l shadow-lg transition-transform duration-300 ease-out", {
        "pointer-events-auto right-0 translate-x-0": isOpen,
        "pointer-events-none right-9 translate-x-[calc(100%+2.25rem)]": t12
      });
      $[30] = isOpen;
      $[31] = t12;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] !== sectionControllers[0]?.listPreferences || $[34] !== summaryProgress) {
      t14 = summaryProgress ? <><ImportQueueProgressSummary progress={summaryProgress} onShowFailed={sectionControllers[0]?.listPreferences?.onFailedOnlyChange ? () => sectionControllers[0]?.listPreferences?.onFailedOnlyChange?.(true) : undefined} /><Separator /></> : null;
      $[33] = sectionControllers[0]?.listPreferences;
      $[34] = summaryProgress;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    let t15;
    if ($[36] !== multiSection || $[37] !== sectionControllers) {
      let t16;
      if ($[39] !== multiSection) {
        t16 = controller => <ImportQueueSection key={controller.domainKey} controller={controller} showHeader={multiSection} showEmptyState={controller.items.length === 0} showListControlsInSection={multiSection} />;
        $[39] = multiSection;
        $[40] = t16;
      } else {
        t16 = $[40];
      }
      t15 = sectionControllers.map(t16);
      $[36] = multiSection;
      $[37] = sectionControllers;
      $[38] = t15;
    } else {
      t15 = $[38];
    }
    let t16;
    if ($[41] !== t15) {
      t16 = <ul className="flex min-h-0 flex-1 scrollbar-thin flex-col gap-1 overflow-y-auto p-2" role="list">{t15}</ul>;
      $[41] = t15;
      $[42] = t16;
    } else {
      t16 = $[42];
    }
    let t17;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <Separator data-sentry-element="Separator" data-sentry-source-file="import-queue-rail.tsx" />;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    let t18;
    if ($[44] !== footerListControlsController) {
      t18 = footerListControlsController ? <ImportQueueListControls controller={footerListControlsController} /> : null;
      $[44] = footerListControlsController;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] !== setOpen) {
      t19 = () => setOpen(false);
      $[46] = setOpen;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    let t20;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <List className="text-muted-foreground size-4" aria-hidden={true} data-sentry-element="List" data-sentry-source-file="import-queue-rail.tsx" />;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    let t21;
    if ($[49] !== t19) {
      t21 = <div className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-lg"><Button type="button" variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground size-8 shrink-0" onClick={t19} aria-label="Hide imports" data-sentry-element="Button" data-sentry-source-file="import-queue-rail.tsx">{t20}</Button></div>;
      $[49] = t19;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    let t22;
    if ($[51] !== footerTitle) {
      t22 = <h2 className="text-foreground truncate text-sm leading-tight font-semibold tracking-tight">{footerTitle}</h2>;
      $[51] = footerTitle;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    let t23;
    if ($[53] !== footerSubtitle) {
      t23 = <p className="text-muted-foreground mt-0.5 text-xs leading-snug">{footerSubtitle}</p>;
      $[53] = footerSubtitle;
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    let t24;
    if ($[55] !== t22 || $[56] !== t23) {
      t24 = <div className="min-w-0">{t22}{t23}</div>;
      $[55] = t22;
      $[56] = t23;
      $[57] = t24;
    } else {
      t24 = $[57];
    }
    let t25;
    if ($[58] !== t21 || $[59] !== t24) {
      t25 = <div className="flex items-center justify-between gap-2"><div className="flex min-w-0 items-center gap-2">{t21}{t24}</div></div>;
      $[58] = t21;
      $[59] = t24;
      $[60] = t25;
    } else {
      t25 = $[60];
    }
    let t26;
    if ($[61] !== t18 || $[62] !== t25) {
      t26 = <div className="flex shrink-0 flex-col gap-2 px-3 py-3">{t18}{t25}</div>;
      $[61] = t18;
      $[62] = t25;
      $[63] = t26;
    } else {
      t26 = $[63];
    }
    if ($[64] !== t10 || $[65] !== t11 || $[66] !== t13 || $[67] !== t14 || $[68] !== t16 || $[69] !== t26) {
      t5 = <div id="import-queue-rail-panel" inert={t10} aria-hidden={t11} className={t13}>{t14}{t16}{t17}{t26}</div>;
      $[64] = t10;
      $[65] = t11;
      $[66] = t13;
      $[67] = t14;
      $[68] = t16;
      $[69] = t26;
      $[70] = t5;
    } else {
      t5 = $[70];
    }
    t6 = !isOpen && !isHeaderRoute && hasItems ? <Button type="button" variant="ghost" size="icon" id="import-queue-rail-handle" aria-label="Show imports" aria-expanded={false} aria-controls="import-queue-rail-panel" onClick={() => setOpen(true)} className={cn("border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground pointer-events-auto absolute right-0 bottom-3 z-50 size-8 shrink-0 rounded-l-xl border-y border-l shadow-sm", {
      "text-foreground": anyActiveWork
    })}><span className="relative flex size-full items-center justify-center"><List className="size-4 shrink-0" aria-hidden={true} />{anyActiveWork ? <span className="bg-primary ring-card absolute top-0 right-0 size-1.5 rounded-full ring-2" aria-hidden={true} /> : null}</span></Button> : null;
    $[17] = controllers;
    $[18] = hasItems;
    $[19] = isHeaderRoute;
    $[20] = isOpen;
    $[21] = multiSection;
    $[22] = sectionControllers;
    $[23] = setOpen;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t4 = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t10;
  if ($[71] !== t4 || $[72] !== t5 || $[73] !== t6) {
    t10 = <div className={t4}>{t5}{t6}</div>;
    $[71] = t4;
    $[72] = t5;
    $[73] = t6;
    $[74] = t10;
  } else {
    t10 = $[74];
  }
  let t11;
  if ($[75] !== t10 || $[76] !== t7 || $[77] !== t8 || $[78] !== t9) {
    t11 = <div className={t7} data-sentry-component={t8} data-sentry-source-file={t9}>{t10}</div>;
    $[75] = t10;
    $[76] = t7;
    $[77] = t8;
    $[78] = t9;
    $[79] = t11;
  } else {
    t11 = $[79];
  }
  return t11;
}
function _temp2(c_2) {
  return c_2.hasActiveWork;
}
function _temp(c) {
  return c.items.length > 0;
}